import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, AlertTriangle, ChevronDown, Activity } from 'lucide-react';
import Layout from '../../../layouts/MainLayout';
import { getObjections } from '../../../api';

const ObjectionDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [objectionData, setObjectionData] = useState(null);
  const [openIndex, setOpenIndex] = useState(0); // Allows closing all via null toggle

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await getObjections(id);
        if (res?.data?.success && res?.data?.data) {
          const { summary, details } = res.data.data;

          const filteredObjections = details.filter(d => d.section !== 'introduction' && d.section !== 'conclusion');
          const introRow = details.find(d => d.section === 'introduction');
          const conclusionRow = details.find(d => d.section === 'conclusion');

          const objections = filteredObjections.map((obj, i) => ({
            id: obj.id || i,
            num: i + 1, // Enforce perfect linear serial order
            isResponded: !!obj.response_text,
            status: obj.response_text ? 'Responded' : 'No response filed',
            text: obj.objection_text || 'Actuarial memorandum or justification requested.',
            response: obj.response_text || null,
            letter_date: obj.objection_letter_date || null,
            respond_by_date: obj.respond_by_date || null
          }));

          setObjectionData({
            summary: {
              id: summary.id,
              tracking_number: summary.serff_tracking_id || 'Unknown',
              company: summary.company_names || summary.company_name || 'Unknown',
              state: summary.state || '—',
              toi: summary.toi || '—',
              filing_type: summary.filing_type || '—',
              status: summary.current_status || '—',
              respond_by: summary.respond_by_date || null,
              intro: introRow?.objection_text || summary.intro || null,
              conclusion: conclusionRow?.objection_text || null
            },
            objections
          });
        }
      } catch (err) {
        console.error('Error loading objection details:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const formatDate = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    return isNaN(d.getTime()) ? v : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getStatusColor = (status) => {
    if (!status) return 'pill-outline';
    const s = String(status).toLowerCase();
    if (s.includes('approved') || s.includes('closed') || s.includes('accepted')) return 'pill-green';
    if (s.includes('objection') || s.includes('info requested')) return 'pill-amber';
    if (s.includes('review') || s.includes('submitted')) return 'pill-blue';
    return 'pill-outline';
  };

  if (loading) return <Layout><div className="content" style={{ padding: '40px', textAlign: 'center', color: 'var(--text2)' }}>Loading objection narrative...</div></Layout>;

  if (!objectionData) return (
    <Layout>
      <div className="content" style={{ padding: '40px' }}>
        <div className="report-card" style={{ textAlign: 'center', padding: '60px 0' }}>
          <Activity size={48} color="var(--text3)" style={{ marginBottom: '12px' }} />
          <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--c-black)' }}>Objection Record Not Found</div>
          <div style={{ fontSize: '14px', color: 'var(--text3)', marginBottom: '20px' }}>We could not locate details for this identification reference.</div>
          <button className="btn btn-sm" onClick={() => navigate(-1)}>Go Back</button>
        </div>
      </div>
    </Layout>
  );

  const { summary, objections } = objectionData;

  return (
    <Layout>
      <div className="topbar">
        <div className="topbar-left">
          <button
            onClick={() => {
              sessionStorage.setItem('comply_active_objections_tab', 'Table');
              navigate('/objections?tab=Table');
            }}
            className="link-back"
          >
            <ChevronLeft size={14} /> Back to Objections
          </button>
          <div className="topbar-title" style={{ fontSize: '20px', fontWeight: 700, color: 'var(--c-black)', marginTop: '12px' }}>{summary.tracking_number}</div>
          <div className="topbar-sub" style={{ fontSize: '14px', color: 'var(--text2)' }}>{summary.company} · Objections Breakdown</div>
        </div>
      </div>

      <div className="content">
        <div className="report-card">
          <div className="report-card-title" style={{ marginBottom: '16px', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>Filing Reference</div>
          <div className="table-wrap">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                  <th style={{ padding: '10px 12px', fontSize: '12px', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Tracking #</th>
                  <th style={{ padding: '10px 12px', fontSize: '12px', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>State</th>
                  <th style={{ padding: '10px 12px', fontSize: '12px', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Type</th>
                  <th style={{ padding: '10px 12px', fontSize: '12px', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>TOI</th>
                  <th style={{ padding: '10px 12px', fontSize: '12px', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>SERFF Status</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ padding: '12px', fontSize: '14px', fontWeight: 600, color: 'var(--c-black)', fontFamily: 'var(--mono)' }}>{summary.tracking_number}</td>
                  <td style={{ padding: '12px', fontSize: '14px', color: 'var(--c-black)' }}>{summary.state}</td>
                  <td style={{ padding: '12px', fontSize: '14px', color: 'var(--c-black)' }}>{summary.filing_type}</td>
                  <td style={{ padding: '12px', fontSize: '14px', color: 'var(--c-black)' }}>{summary.toi}</td>
                  <td style={{ padding: '12px' }}>
                    <span className={`pill ${getStatusColor(summary.status)}`}>{summary.status}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {summary.intro && (
          <div className="report-card" style={{ marginTop: '16px', padding: '20px' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text3)', marginBottom: '8px' }}>Introduction</div>
            <div style={{ fontSize: '14px', color: 'var(--c-black)', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{summary.intro}</div>
          </div>
        )}

        <div style={{ marginTop: '24px', marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--c-black)', margin: 0 }}>
            Responses Breakdown ({objections.length})
          </h3>
          {summary.respond_by && (
            <div style={{ fontSize: '13px', color: 'var(--c-orange)', display: 'flex', alignItems: 'center', gap: '6px', background: 'var(--orange-bg)', padding: '4px 10px', borderRadius: '999px', fontWeight: 500 }}>
              <AlertTriangle size={14} />
              Respond by {formatDate(summary.respond_by)}
            </div>
          )}
        </div>

        {objections.length === 0 ? (
          <div className="report-card" style={{ padding: '32px', textAlign: 'center', color: 'var(--text3)' }}>
            No individual objection text recorded for this entry.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '8px' }}>
            {objections.map((obj, index) => {
              const isOpen = openIndex === index;
              return (
                <div
                  key={obj.id}
                  style={{
                    background: 'var(--c-white)',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: isOpen ? '0 4px 12px rgba(0,0,0,0.05)' : 'none',
                    transition: 'box-shadow 0.5s ease'
                  }}
                >
                  {/* Accordion Header */}
                  <div
                    onClick={() => setOpenIndex(isOpen ? null : index)}
                    style={{
                      padding: '20px 20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      background: isOpen ? 'var(--c-white)' : 'var(--c-white)',
                      borderBottom: 'none',
                      transition: 'background-color 0.5s'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '6px',
                        background: obj.isResponded ? 'var(--bg-teal-soft)' : 'var(--bg-orange-soft)',
                        color: obj.isResponded ? 'var(--c-teal-active)' : 'var(--c-orange)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        fontWeight: 700
                      }}>
                        {obj.num}
                      </div>
                      <div>
                        <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--c-black)' }}>Objection {obj.num}</div>
                        {!isOpen && (
                          <div style={{ fontSize: '12px', color: 'var(--text3)' }}>
                            {obj.text.substring(0, 120)}{obj.text.length > 120 ? '...' : ''}
                          </div>
                        )}
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <span className={`pill ${obj.isResponded ? 'pill-green' : 'pill-red'}`} style={{ fontSize: '11px' }}>
                        {obj.status}
                      </span>
                      <ChevronDown
                        size={18}
                        style={{
                          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.5s ease',
                          color: 'var(--text3)'
                        }}
                      />
                    </div>
                  </div>

                  {/* Accordion Body with Transition Wrapper */}
                  <div
                    style={{
                      maxHeight: isOpen ? '1200px' : '0px',
                      opacity: isOpen ? 1 : 0,
                      overflow: 'hidden',
                      transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                      background: 'var(--c-white)'
                    }}
                  >
                    <div style={{ padding: '20px', borderTop: '1px solid var(--border)' }}>
                      <div style={{ marginBottom: '20px' }}>
                        <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text3)', marginBottom: '8px' }}>Objection Issued</div>
                        <div style={{
                          padding: '16px',
                          background: 'var(--c-white)',
                          border: '1px solid var(--c-white)',
                          borderRadius: '8px',
                          fontSize: '14px',
                          color: 'var(--c-black)',
                          lineHeight: '1.6',
                          whiteSpace: 'pre-wrap'
                        }}>
                          {obj.text}
                        </div>
                      </div>

                      <div>
                        <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text3)', marginBottom: '8px' }}>Response Filed</div>
                        {obj.response ? (
                          <div style={{
                            padding: '16px',
                            background: 'var(--bg-teal-soft)',
                            border: '1px solid var(--bg-teal-soft)',
                            borderRadius: '8px',
                            fontSize: '14px',
                            color: 'var(--c-teal-active)',
                            lineHeight: '1.6',
                            whiteSpace: 'pre-wrap'
                          }}>
                            {obj.response}
                          </div>
                        ) : (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '14px 16px',
                            background: 'var(--bg-orange-soft)',
                            border: '1px solid var(--bg-orange-soft)',
                            borderRadius: '8px',
                            fontSize: '13px',
                            color: 'var(--c-orange)'
                          }}>
                            <AlertTriangle size={16} />
                            <span>No response recorded yet. Please address within allocated timeframe.</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {summary.conclusion && (
          <div className="report-card" style={{ marginTop: '16px', padding: '20px', background: 'var(--c-white)', border: '1px dashed var(--border)' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text3)', marginBottom: '8px' }}>Conclusion</div>
            <div style={{ fontSize: '14px', color: 'var(--c-black)', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{summary.conclusion}</div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ObjectionDetails;

